import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import core, { App, GQL } from '@gin-betting/core';
import { createStructuredSelector } from 'reselect';

import { ACTIVE_MARKET_STATUSES } from 'app-constants';
import { useMemoSelector, useWithLocation } from 'hooks';

const {
    entities: {
        selectors: { selectSlug, makeMatchSelector, makeTournamentSelector },
        actions: { getMatchBySlug, getTournamentBySlug },
    },
    utils: { checkOnRoute },
} = core;

enum EntityType {
    SportEvent,
    Tournament,
}

interface Props {
    slug: string;
    sportEventId?: string;
    tournamentId?: string;
    children: (entityId: string) => any;
}

const useEntityType = (): EntityType | null => {
    const eventType = useWithLocation(() => getEntityType());

    return eventType;
};

const EntityIdResolver: React.FC<Props> = ({
    sportEventId,
    tournamentId,
    slug,
    children,
}) => {
    const dispatch = useDispatch();
    const entityType = useEntityType();
    const { sportEvent, tournament, slugEntity } = useMemoSelector(
        makePropsSelector(),
        {
            slug,
            matchId: sportEventId,
            tournamentId,
        }
    );

    useEffect(() => {
        switch (entityType) {
            case EntityType.SportEvent: {
                dispatch(
                    getMatchBySlug({
                        slug,
                        marketStatuses: getMarketStatus(),
                    })
                );

                break;
            }

            case EntityType.Tournament: {
                dispatch(
                    getTournamentBySlug({
                        slug,
                    })
                );
                break;
            }

            default: {
                break;
            }
        }
    }, [dispatch, slug, entityType]);

    switch (entityType) {
        case EntityType.SportEvent: {
            const id = sportEvent?.id || slugEntity?.matchId;

            return id ? children(id) : null;
        }

        case EntityType.Tournament: {
            const id = tournament?.id || slugEntity?.tournamentId;

            return id ? children(id) : null;
        }

        default: {
            return children(slug);
        }
    }
};

function getMarketStatus(): GQL.MarketStatus[] {
    return checkOnRoute(App.Routes.RESULT_MATCH)
        ? [GQL.MarketStatus.RESULTED]
        : ACTIVE_MARKET_STATUSES;
}

function getEntityType(): EntityType | null {
    if (
        checkOnRoute(App.Routes.MATCH) ||
        checkOnRoute(App.Routes.OUTRIGHT) ||
        checkOnRoute(App.Routes.RESULT_MATCH)
    ) {
        return EntityType.SportEvent;
    }

    if (checkOnRoute(App.Routes.TOURNAMENT)) {
        return EntityType.Tournament;
    }

    return null;
}

const entityPropsToPick = ['id' as const];

const makePropsSelector = () => {
    const selectorTournament = makeTournamentSelector({
        propsToPick: entityPropsToPick,
    });
    const selectSportEvent = makeMatchSelector({
        propsToPick: entityPropsToPick,
    });

    return createStructuredSelector({
        slugEntity: selectSlug,
        sportEvent: selectSportEvent,
        tournament: selectorTournament,
    });
};

export default EntityIdResolver;
